import "./TemplateSelect.scss";

import { Flex, Input, Menu, SearchIcon, Text, tabListBehavior } from "@fluentui/react-northstar";
import { QuestionnaireTemplateDTO } from "@teams/types/api";
import React, { useState } from "react";

import { PollyIcon, PollyIconName } from "../../../../_shared/components/PollyIcon";
import { TemplateCardList, TemplateCardListCompactSettings } from "./TemplateCardList";

type Props = {
  templateScope: "all" | "global" | "personal";
  templateActionLabel: string;
  isCompact?: boolean;
  compactSettings?: TemplateCardListCompactSettings;
  onSelected: (template: QuestionnaireTemplateDTO) => void;
};

export const TemplateSelect: React.FC<Props> = ({
  templateScope,
  templateActionLabel,
  isCompact,
  compactSettings,
  onSelected,
}) => {
  const [searchValue, setSearchValue] = useState<string>();
  const [templateSection, setTemplateSection] = useState<"global" | "personal">("global");
  const [filteredResultCountGlobal, setFilteredResultCountGlobal] = useState<number>(0);
  const [filteredResultCountPersonal, setFilteredResultCountPersonal] = useState<number>(0);

  const templateMenuItems = [
    {
      key: "global",
      content: (
        <TabMenuItem
          filteredResultCount={filteredResultCountGlobal}
          searchFilter={searchValue}
          highlighted={templateSection === "global" ? true : false}
          iconName="global-templates-icon"
          label={isCompact ? "Global" : "Global Templates"}
        />
      ),
      onClick: () => setTemplateSection("global"),
    },
    {
      key: "personal",
      content: (
        <TabMenuItem
          filteredResultCount={filteredResultCountPersonal}
          searchFilter={searchValue}
          highlighted={templateSection === "personal" ? true : false}
          iconName="my-templates-icon"
          label={isCompact ? "Personal" : "My Templates"}
        />
      ),
      onClick: () => setTemplateSection("personal"),
    },
  ];

  return (
    <Flex column gap="gap.large">
      <Flex.Item push>
        <div>
          <Input
            icon={<SearchIcon />}
            clearable
            fluid
            placeholder="Search Templates..."
            onChange={(_e, d) => {
              if (!d?.value) {
                setSearchValue(undefined);
              } else {
                setSearchValue(d.value.trim());
              }
            }}
          />
        </div>
      </Flex.Item>
      <Menu
        defaultActiveIndex={0}
        items={templateMenuItems}
        primary
        underlined
        className="template-select__menu"
        accessibility={tabListBehavior}
      />
      {/* With conditional rendering the data for both the tabs is reloaded on tab change.
        This gives a ~1-2 second loading delay every time the tabs are changed. it better to
        keep both the elements loaded and conditionally visible because they 
        are rather small components usually rendering <100 element lists so they shouldn't 
        have a significant overhead. */}
      <div style={{ display: templateSection === "global" ? undefined : "none" }}>
        {(templateScope === "all" || templateScope === "global") && (
          <TemplateCardList
            searchFilter={searchValue}
            handleSearchResultCount={setFilteredResultCountGlobal}
            scope="global"
            onSelect={onSelected}
            actionLabel={templateActionLabel}
            compactSettings={compactSettings}
            isCompact={isCompact}
          />
        )}
      </div>
      <div style={{ display: templateSection === "personal" ? undefined : "none" }}>
        {(templateScope === "all" || templateScope === "personal") && (
          <TemplateCardList
            searchFilter={searchValue}
            handleSearchResultCount={setFilteredResultCountPersonal}
            scope="personal"
            onSelect={onSelected}
            actionLabel={templateActionLabel}
            compactSettings={compactSettings}
            isCompact={isCompact}
          />
        )}
      </div>
    </Flex>
  );
};

type TabMenuItemProps = {
  filteredResultCount: number;
  searchFilter: string | undefined;
  highlighted: boolean;
  iconName: PollyIconName;
  label: string;
};

const TabMenuItem: React.FC<TabMenuItemProps> = ({
  filteredResultCount,
  searchFilter,
  iconName,
  label,
}) => {
  return (
    <Flex className="template-select__menu-item" gap="gap.small" vAlign="center">
      <PollyIcon ignoreTheme size="small" inheritColor icon={iconName} />
      <Text content={label} />
      {filteredResultCount > 0 && searchFilter && (
        <Flex vAlign="center" hAlign="center" className="template-select__badge">
          <Text content={filteredResultCount} size="medium" weight="semibold" />
        </Flex>
      )}
    </Flex>
  );
};
